import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import { useInstanceMode, useSetInstanceMode } from "@/lib/settings";
import { SectionHeader } from "@/components/application/section-headers/section-headers";

/**
 * Token Settings — instance mode (SINGLE/MULTI). Přepnutí MULTI→SINGLE deaktivuje
 * přebytečné aktivní instance na licenci (potvrzení + výsledek úklidu).
 */
export function TokenSettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useInstanceMode();
  const set = useSetInstanceMode();
  const [confirmMode, setConfirmMode] = useState<null | "SINGLE_INSTANCE" | "MULTI_INSTANCE">(null);

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("tokenSettings.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("tokenSettings.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-tertiary">{t("tokenSettings.currentMode")}:</span>
            <Badge type="pill-color" size="sm" color={data.currentMode === "SINGLE_INSTANCE" ? "blue" : "success"}>
              {data.currentMode}
            </Badge>
          </div>
          <p className="text-sm text-secondary">{data.description}</p>
          <div className="text-xs text-tertiary">
            {t("tokenSettings.buildProfile")}: {data.buildProfile} · {t("tokenSettings.default")}: {data.defaultMode}
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              color={data.currentMode === "SINGLE_INSTANCE" ? "primary" : "secondary"}
              isDisabled={data.currentMode === "SINGLE_INSTANCE" || set.isPending}
              onPress={() => setConfirmMode("SINGLE_INSTANCE")}
            >
              {t("tokenSettings.setSingle")}
            </Button>
            <Button
              size="sm"
              color={data.currentMode === "MULTI_INSTANCE" ? "primary" : "secondary"}
              isDisabled={data.currentMode === "MULTI_INSTANCE" || set.isPending}
              onPress={() => setConfirmMode("MULTI_INSTANCE")}
            >
              {t("tokenSettings.setMulti")}
            </Button>
          </div>

          {set.data?.cleanupStats && (
            <p className="text-sm text-warning-primary">
              {t("tokenSettings.cleanupDone", {
                tokens: set.data.cleanupStats.deactivatedTokens,
                licences: set.data.cleanupStats.affectedLicences,
              })}
            </p>
          )}
        </div>
      )}

      {confirmMode && (
        <AppDialog
          isOpen
          onClose={() => setConfirmMode(null)}
          title={t("tokenSettings.switchTitle")}
          description={
            confirmMode === "SINGLE_INSTANCE" ? t("tokenSettings.switchSingleWarn") : t("tokenSettings.switchMultiInfo")
          }
          footer={
            <>
              <Button color="secondary" onPress={() => setConfirmMode(null)} isDisabled={set.isPending}>{t("common.cancel")}</Button>
              <Button color="primary" onPress={async () => { await set.mutateAsync(confirmMode); setConfirmMode(null); }} isDisabled={set.isPending}>
                {t("common.confirm")}
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}
